import React, {useEffect, useRef, useState} from 'react';
import PropTypes from "prop-types";
import * as Icon from 'ubs-icons';
import useDebouncedEffect from "use-debounced-effect";
import {LIMIT} from "../../../common/constant";
import useEscKey from "ubs-framework/v2/hooks/useEscKey";

const UFilterSearch = React.memo((props) => {
    const ref = useRef(null);
    const [search, setSearch] = useState(props?.search || '');

    useEffect(() => {
        if (typeof props?.search === 'string') setSearch(props?.search);
    }, [props?.search]);

    useEffect(() => {
        if(props?.focus) ref?.current?.focus()
    }, [props?.focus]);

    useEffect(() => {
        const handleKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.code === 'KeyJ') {
                e.preventDefault();
                ref?.current?.focus();
            }
        };
        document.addEventListener('keydown', handleKey, true);
        return () => document.removeEventListener('keydown', handleKey, true);
    }, []);

    useEscKey(() => {
        if (props?.closeOnEsc && ref?.current === document.activeElement) {
            setSearch('');
            ref?.current?.blur();
        }
    }, {useCapture: props?.useCaptureEsc});

    useDebouncedEffect(() => {
        if (!props?.onSearch) return;
        const min = props?.skipSearchCharacter ? 0 : (props?.minLength ?? props?.limit ?? LIMIT.SEARCH_CHARACTER);
        if (search?.length === 0 || search?.length >= min) props.onSearch(search);
    }, props?.debounce ?? LIMIT.DE_BOUNCING_TIME_OUT, [search]);

    const onChange = (e) => {
        e?.stopPropagation?.();
        const value = props?.stripLeadingSpace ? e.target.value.replace(/^\s+/g, '') : e.target.value;
        setSearch(value);
    };

    const onKeyDown = (e) => {
        if (props?.onKeyDown) props.onKeyDown(e);
    };

    const clear = () => {
        if (!props?.disabled) setSearch('');
    };

    const iconFill = search?.length !== 0 ? '#007AFF' : '#4E525F';

    return (
        <div className={`u_filter u_filter-search ${search?.length !== 0 ? 'active' : ''}`}>
            <span className="u_search-icon u_cp" onClick={() => ref?.current?.focus()}><Icon.Search size={14} fill={iconFill}/></span>
            <input type="text" tabIndex="0" className="u_input" placeholder={props?.placeholder || 'Search'} ref={ref} value={search} onChange={onChange} onKeyDown={onKeyDown} onBlur={props?.blur} autoFocus={props?.autoFocus} autoComplete="off" disabled={props?.disabled}/>
            {search?.length !== 0 && (
                <span className="u_delete-icon u_cp" onClick={clear}><Icon.Close size={14} fill={iconFill}/></span>
            )}
        </div>
    );
});

UFilterSearch.propTypes = {
    search: PropTypes.string,
    onSearch: PropTypes.func,
    placeholder: PropTypes.string,
    closeOnEsc: PropTypes.bool,
    useCaptureEsc: PropTypes.bool,
    minLength: PropTypes.number,
    limit: PropTypes.number,
    stripLeadingSpace: PropTypes.bool,
    disabled: PropTypes.bool,
    focus: PropTypes.bool,
    autoFocus: PropTypes.bool,
    skipSearchCharacter: PropTypes.bool,
    blur: PropTypes.func,
    onKeyDown: PropTypes.func,
    debounce: PropTypes.number,
};

UFilterSearch.defaultProps = {
    search: '',
    onSearch: () => {},
    placeholder: 'Search',
    closeOnEsc: true,
    useCaptureEsc: true,
    minLength: LIMIT.SEARCH_CHARACTER,
    stripLeadingSpace: true,
    disabled: false,
    focus: false,
    autoFocus: false,
    skipSearchCharacter: false,
    debounce: LIMIT.DE_BOUNCING_TIME_OUT,
};

export default UFilterSearch;
